import { useRef, useCallback, useEffect, useState } from 'react';
import { useSessionStore } from '@/state/session-store';

interface LineInfo {
  text: string;
  indent: number;
  isHeader: boolean;
}

/**
 * Process pasted text to preserve structure from rich text sources
 * Handles bullets, numbered lists, and indentation from Word/Google Docs/etc.
 */
function processPastedText(text: string): string {
  // Normalize line endings
  let processed = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Split into lines for processing
  const lines = processed.split('\n');

  // Patterns for detecting structure
  // Level 1 bullets: ●, •, ■, ▪
  const level1Bullets = /^[\s]*[●•■▪]\s*/;
  // Level 2 bullets: ○, ◦, □, ▫, ▸
  const level2Bullets = /^[\s]*[○◦□▫▸]\s*/;
  // Level 3+ bullets: -, *
  const level3Bullets = /^[\s]*[-*]\s+/;
  // "o " at start of line followed by capital (common when ○ becomes plain text)
  const plainOBullet = /^[\s]*o\s+(?=[A-Z])/;
  // Numbered list patterns
  const numberedL1 = /^[\s]*\d+[.)]\s*/;
  const numberedL2 = /^[\s]*[a-z][.)]\s*/i;

  // First pass: detect which lines start new items vs. are continuations
  const isNewItem = (line: string): boolean => {
    const trimmed = line.trim();
    if (!trimmed) return false;

    // Lines starting with bullets/numbers are new items
    if (level1Bullets.test(line)) return true;
    if (level2Bullets.test(line)) return true;
    if (level3Bullets.test(line)) return true;
    if (plainOBullet.test(line)) return true;
    if (numberedL1.test(line)) return true;
    if (numberedL2.test(line)) return true;

    // Lines that look like headers are new items
    if (/:\s*$/.test(trimmed)) return true; // Ends with colon
    if (/^[A-Z][A-Z\s]+$/.test(trimmed) && trimmed.length < 50) return true; // ALL CAPS

    // Lines starting with significant indentation are likely new items
    const leadingSpaces = (line.match(/^[\s]*/)?.[0] || '').length;
    if (leadingSpaces >= 4) return true;

    return false;
  };

  // Second pass: merge continuation lines and extract structure
  const mergedLines: { text: string; bulletType: string | null }[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Empty lines act as paragraph separators
    if (!trimmed) {
      mergedLines.push({ text: '', bulletType: null });
      continue;
    }

    // Determine bullet type for this line
    let bulletType: string | null = null;
    if (level1Bullets.test(line)) bulletType = 'l1';
    else if (level2Bullets.test(line)) bulletType = 'l2';
    else if (plainOBullet.test(line)) bulletType = 'l2'; // "o " -> level 2
    else if (level3Bullets.test(line)) bulletType = 'l3';
    else if (numberedL1.test(line)) bulletType = 'n1';
    else if (numberedL2.test(line)) bulletType = 'n2';

    if (isNewItem(line) || mergedLines.length === 0 || mergedLines[mergedLines.length - 1].text === '') {
      // Start a new item
      mergedLines.push({ text: trimmed, bulletType });
    } else {
      // This is a continuation of the previous line
      // Join with a space (the previous line probably ended mid-sentence)
      const lastItem = mergedLines[mergedLines.length - 1];
      lastItem.text = lastItem.text + ' ' + trimmed;
    }
  }

  // Third pass: clean bullets and apply indentation
  const result: string[] = [];

  for (const item of mergedLines) {
    if (!item.text) {
      result.push('');
      continue;
    }

    let cleanText = item.text;
    let indent = '';

    // Remove bullet characters and set indentation based on bullet type
    if (item.bulletType === 'l1') {
      cleanText = cleanText.replace(/^[●•■▪]\s*/, '');
      indent = '';
    } else if (item.bulletType === 'l2') {
      cleanText = cleanText.replace(/^[○◦□▫▸]\s*/, '').replace(/^o\s+/, '');
      indent = '\t';
    } else if (item.bulletType === 'l3') {
      cleanText = cleanText.replace(/^[-*]\s+/, '');
      indent = '\t\t';
    } else if (item.bulletType === 'n1') {
      cleanText = cleanText.replace(/^\d+[.)]\s*/, '');
      indent = '';
    } else if (item.bulletType === 'n2') {
      cleanText = cleanText.replace(/^[a-z][.)]\s*/i, '');
      indent = '\t';
    }

    // Clean any remaining bullet-like prefixes
    cleanText = cleanText.replace(/^[○◦●•■▪□▫▸]\s*/, '');

    if (cleanText) {
      result.push(indent + cleanText);
    }
  }

  // Join and clean up multiple blank lines
  return result.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Detect if a line looks like a group header
 */
function isGroupHeaderLine(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;

  // Lines starting with bullets/dashes are NOT headers
  if (/^[-*•◦▪▸]/.test(trimmed)) return false;

  // Lines starting with numbers are NOT headers
  if (/^[\d]+[.)]/.test(trimmed)) return false;

  // Lines starting with semantic sub-item indicators are NOT headers
  if (/^(Example|Important|Note|Why|How|Tip|Warning|Info):/i.test(trimmed)) return false;

  // ALL CAPS lines are headers
  const letters = trimmed.replace(/[^a-zA-Z]/g, '');
  if (letters.length >= 3) {
    const upperCount = (trimmed.match(/[A-Z]/g) || []).length;
    const lowerCount = (trimmed.match(/[a-z]/g) || []).length;
    if (upperCount > 0 && lowerCount === 0) return true;
    if (upperCount > lowerCount * 2) return true;
  }

  // Lines ending with colon are headers
  if (/:\s*$/.test(trimmed)) return true;

  // "Topic - description" pattern
  const dashMatch = trimmed.match(/^([A-Z][^-]+)\s*-\s*.+/);
  if (dashMatch && dashMatch[1].trim().length < 30) return true;

  // Short capitalized lines without punctuation = likely header
  const hasLeadingWhitespace = /^[\t ]/.test(line);
  if (!hasLeadingWhitespace && trimmed.length < 40 && !/[.!?]$/.test(trimmed)) {
    if (/^[A-Z]/.test(trimmed) && !/^(I |We |You |The |A |An |This |That |First|Second|Third)/.test(trimmed)) {
      return true;
    }
  }

  return false;
}

/**
 * Count indentation level from whitespace
 */
function getIndentLevel(line: string): number {
  const match = line.match(/^([\t ]*)/);
  if (!match) return 0;
  const whitespace = match[1];
  const tabCount = (whitespace.match(/\t/g) || []).length;
  const spaceCount = (whitespace.match(/ /g) || []).length;
  return tabCount + Math.floor(spaceCount / 2);
}

/**
 * Parse lines into structured info for preview
 */
function parseLines(text: string): LineInfo[] {
  return text.split('\n').map(line => {
    const trimmed = line.trim();
    if (!trimmed) {
      return { text: '', indent: 0, isHeader: false };
    }
    return {
      text: trimmed,
      indent: isGroupHeaderLine(line) ? 0 : Math.max(getIndentLevel(line), 1),
      isHeader: isGroupHeaderLine(line),
    };
  });
}

const PLACEHOLDER = `Introduction and company overview
Pain points we solve:
- Customer problem #1
- Customer problem #2
Product Demo:
- Feature walkthrough
- Integration capabilities
Pricing and packaging
Q&A and next steps`;

interface AgendaEditorProps {
  className?: string;
  minHeight?: number;
  maxHeight?: number;
}

export function AgendaEditor({ className = '', minHeight = 300, maxHeight = 600 }: AgendaEditorProps) {
  const { agendaRawText, setAgendaFromText } = useSessionStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [lineInfos, setLineInfos] = useState<LineInfo[]>([]);

  // Parse lines whenever text changes
  useEffect(() => {
    setLineInfos(parseLines(agendaRawText));
  }, [agendaRawText]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAgendaFromText(e.target.value);
  };

  /**
   * Handle paste to preserve structure from rich text
   */
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = e.clipboardData.getData('text/plain');
    if (!pastedText) return;

    e.preventDefault();

    const textarea = e.currentTarget;
    const { selectionStart, selectionEnd, value } = textarea;

    // Process the pasted text to preserve structure
    const processedText = processPastedText(pastedText);

    // Insert at cursor position, replacing any selection
    const newText = value.substring(0, selectionStart) + processedText + value.substring(selectionEnd);
    setAgendaFromText(newText);

    // Move cursor to end of pasted content
    setTimeout(() => {
      const newPosition = selectionStart + processedText.length;
      textarea.selectionStart = textarea.selectionEnd = newPosition;
      textarea.focus();
    }, 0);
  }, [setAgendaFromText]);

  /**
   * Handle Tab/Shift+Tab for indentation
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Tab') return;

    e.preventDefault();
    const textarea = e.currentTarget;
    const { selectionStart, selectionEnd, value } = textarea;

    // Find the start and end of the current line(s)
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const lineEnd = value.indexOf('\n', selectionEnd);
    const effectiveLineEnd = lineEnd === -1 ? value.length : lineEnd;

    // Check if multiple lines are selected
    const selectedText = value.substring(lineStart, effectiveLineEnd);
    const lines = selectedText.split('\n');

    if (e.shiftKey) {
      // Shift+Tab: Remove indentation
      const modifiedLines = lines.map(line => {
        if (line.startsWith('\t')) {
          return line.substring(1);
        } else if (line.startsWith('  ')) {
          return line.substring(2);
        }
        return line;
      });

      const newText = value.substring(0, lineStart) + modifiedLines.join('\n') + value.substring(effectiveLineEnd);
      setAgendaFromText(newText);

      // Restore cursor position (adjusted for removed characters)
      const removedChars = selectedText.length - modifiedLines.join('\n').length;
      setTimeout(() => {
        textarea.selectionStart = Math.max(lineStart, selectionStart - (lines[0].length !== modifiedLines[0].length ? (lines[0].startsWith('\t') ? 1 : 2) : 0));
        textarea.selectionEnd = selectionEnd - removedChars;
      }, 0);
    } else {
      // Tab: Add indentation
      if (selectionStart === selectionEnd) {
        // No selection: insert tab at cursor
        const newText = value.substring(0, selectionStart) + '\t' + value.substring(selectionEnd);
        setAgendaFromText(newText);
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = selectionStart + 1;
        }, 0);
      } else {
        // Selection: indent all selected lines
        const modifiedLines = lines.map(line => '\t' + line);
        const newText = value.substring(0, lineStart) + modifiedLines.join('\n') + value.substring(effectiveLineEnd);
        setAgendaFromText(newText);

        // Adjust selection to cover indented lines
        setTimeout(() => {
          textarea.selectionStart = lineStart;
          textarea.selectionEnd = lineStart + modifiedLines.join('\n').length;
        }, 0);
      }
    }
  }, [setAgendaFromText]);

  // Get stats
  const nonEmptyLines = lineInfos.filter(l => l.text);
  const headerCount = nonEmptyLines.filter(l => l.isHeader).length;
  const itemCount = nonEmptyLines.filter(l => !l.isHeader).length;

  return (
    <div className={`relative ${className}`}>
      {/* Line numbers and visual indicators */}
      <div className="absolute left-0 top-0 bottom-0 w-10 bg-slate-800 border-r border-slate-600 pointer-events-none overflow-hidden rounded-l-lg">
        <div className="pt-3 pr-2 text-right font-mono text-xs text-slate-500 leading-6">
          {lineInfos.map((info, i) => (
            <div key={i} className={info.isHeader ? 'text-blue-400 font-bold' : ''}>
              {i + 1}
            </div>
          ))}
        </div>
      </div>

      {/* Main textarea */}
      <textarea
        ref={textareaRef}
        value={agendaRawText}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={PLACEHOLDER}
        spellCheck={false}
        className="w-full pl-12 pr-4 py-3 bg-slate-800 text-slate-100 text-sm font-mono
                   border border-slate-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent
                   placeholder-slate-500 resize-none leading-6"
        style={{ minHeight, maxHeight }}
      />

      {/* Stats bar */}
      <div className="absolute bottom-2 right-3 flex items-center gap-3 text-xs">
        {itemCount > 0 && (
          <>
            <span className="text-green-400">{itemCount} talking point{itemCount !== 1 ? 's' : ''}</span>
            {headerCount > 0 && (
              <span className="text-blue-400">{headerCount} section{headerCount !== 1 ? 's' : ''}</span>
            )}
          </>
        )}
        {itemCount === 0 && (
          <span className="text-slate-500">Tab to indent, Shift+Tab to outdent</span>
        )}
      </div>
    </div>
  );
}
